import { getUILanguage, Lib, translation } from "albertine-shared-web";
import classNames from "classnames";
import { format, isSameYear, parse } from "date-fns";
import type React from "react";
import type { PropsWithChildren } from "react";
import { Flex, type Gap } from "./Flex";
import Label from "./Label";

const noDateKey = "no-date";

type TimelineProps<T> = {
    items: T[];
    order: "newest-first" | "oldest-first";
    wrapperGap: Gap;
    monthGap: Gap;
    itemGap: Gap;
    renderItem: (item: T, index: number) => React.ReactNode;
    className?: string;
    labelComponent: React.ComponentType<
        PropsWithChildren<{ className?: string; children: React.ReactNode }>
    >;
};

export type TimelineItemDateProps<T> = {
    getDate: (data: T) => Date | undefined;
};

// "2025-01"
function createDateKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function groupByMonthAndSort<T extends TimelineItemDateProps<T>>(
    data: T[],
): Record<string, T[]> {
    return data.reduce<Record<string, T[]>>((acc, item) => {
        const date = item.getDate(item);
        if (date) {
            const key = createDateKey(date);

            if (!acc[key]) acc[key] = [];
            acc[key].push(item);
        } else {
            if (!acc[noDateKey]) acc[noDateKey] = [];
            acc[noDateKey].push(item);
        }
        return acc;
    }, {});
}

function formatTimelineTitle(key: string): string {
    if (key === noDateKey) {
        return translation("no-date");
    }

    const locale = getUILanguage().dateFnsLocale;
    const date = parse(key, "yyyy-MM", new Date());
    const isCurrentYear = isSameYear(Date.now(), date);

    return isCurrentYear
        ? format(date, "MMMM", {
              locale,
          })
        : format(date, "MMMM yyyy", {
              locale,
          });
}

/**
 * Renders a list of items grouped by month in a timeline format.
 * It's headless, relying on passed classNames for styling.
 */
function Timeline<T extends { id: string } & TimelineItemDateProps<T>>({
    className,
    monthGap = "value16",
    itemGap = "value16",
    wrapperGap = "value32",
    items,
    renderItem,
    order = "newest-first",
    labelComponent: LabelComponent = Label.Small.MediumBold.Text2,
}: TimelineProps<T>) {
    // Sort items once based on date
    const newestFirstItems = items.sort((a, b) =>
        Lib.Utils.Sorting.sortByDateComparator<T>(
            (item) => item?.getDate(item),
            order === "oldest-first", // This is so that undefined dates are always last
        )(a, b),
    );
    // Apply requested order
    const sortedItems =
        order === "newest-first" ? newestFirstItems : [...newestFirstItems].reverse(); // Use spread to avoid mutating the original sorted array

    // Group sorted items by month
    const monthlyGrouped = groupByMonthAndSort<T>(sortedItems);
    const monthKeys = Object.keys(monthlyGrouped);

    return (
        <Flex.Column className={classNames("timeline", className)} gap={wrapperGap}>
            {monthKeys.map((key) => {
                const groupItems = monthlyGrouped[key];
                return (
                    <Flex.Column key={key} gap={monthGap}>
                        <Flex.Row
                            gap="value16"
                            alignItems="center"
                            className={"timeline__month"}
                        >
                            <span className={"timeline__month-point"} />
                            <LabelComponent className={"timeline__month-title"}>
                                {formatTimelineTitle(key)}
                            </LabelComponent>
                        </Flex.Row>

                        {groupItems.map((item, index) => (
                            <Flex.Row
                                key={`timeline-item__${item.id}`}
                                className={"timeline-item"}
                                alignItems="start"
                                gap={itemGap}
                            >
                                {renderItem(item, index)}
                            </Flex.Row>
                        ))}
                    </Flex.Column>
                );
            })}
        </Flex.Column>
    );
}

export default Timeline;
